import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, 
  Circle, 
  ArrowRight, 
  Clock,
  CreditCard,
  AlertCircle,
  Lock,
  Briefcase,
  FileText,
  Calendar,
  Loader
} from 'lucide-react';
import workAbroadService from '../../services/workAbroadService';
import PaymentModal from './PaymentModal';
import { useAuth } from '../../context/AuthContext';

const WorkOverviewSection = ({ overview, profile, onRefresh, onNavigate }) => {
  const { user } = useAuth();
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const getPipelineStatusDisplay = (status) => {
    const statusMap = {
      'consultation_scheduled': { label: 'Consultation Scheduled', color: 'blue' },
      'consultation_completed': { label: 'Consultation Completed', color: 'green' },
      'onboarding': { label: 'Onboarding', color: 'blue' },
      'payment_pending': { label: 'Payment Pending', color: 'yellow' },
      'documents_pending': { label: 'Documents Pending', color: 'yellow' },
      'documents_under_review': { label: 'Documents Under Review', color: 'blue' },
      'documents_approved': { label: 'Documents Approved', color: 'green' },
      'interview_preparation': { label: 'Interview Preparation', color: 'blue' },
      'interview_scheduled': { label: 'Interview Scheduled', color: 'purple' },
      'interview_completed': { label: 'Interview Completed', color: 'green' },
      'offer_received': { label: 'Offer Received', color: 'green' },
      'offer_accepted': { label: 'Offer Accepted', color: 'green' },
      'completed': { label: 'Completed', color: 'green' }
    };

    return statusMap[status] || { label: status, color: 'gray' };
  };

  const statusDisplay = getPipelineStatusDisplay(profile?.pipelineStatus);
  const statusColors = {
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    green: 'bg-green-100 text-green-700 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200'
  };

  const journeySteps = [
    {
      id: 'personal-details',
      title: 'Personal Details',
      description: 'Complete your basic information',
      icon: CheckCircle,
      status: 'completed',
      locked: false
    },
    {
      id: 'work-history',
      title: 'Work History',
      description: 'Add your work experience',
      icon: Briefcase,
      status: 'in-progress',
      locked: false
    },
    {
      id: 'role-preferences',
      title: 'Role Preferences',
      description: 'Select your preferred roles',
      icon: Circle,
      status: 'pending',
      locked: false
    },
    {
      id: 'payment',
      title: 'Payment',
      description: 'Unlock interview access',
      icon: CreditCard,
      status: 'pending',
      locked: false
    },
    {
      id: 'documents',
      title: 'Documents',
      description: 'Upload required documents',
      icon: FileText,
      status: 'pending',
      locked: profile?.paymentStatus !== 'paid'
    },
    {
      id: 'interview',
      title: 'Interview',
      description: 'Schedule and attend interview',
      icon: Calendar,
      status: 'pending',
      locked: profile?.paymentStatus !== 'paid'
    }
  ];

  return (
    <div className="p-6 lg:p-8">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-2xl font-bold text-[#202356] mb-2">
          Hello{user?.firstName ? `, ${user.firstName}` : ''}!
        </h1>
        <p className="text-gray-600">
          Your structured pathway to global employment opportunities
        </p>
      </motion.div>

      {/* Current Status Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mt-6 bg-white rounded-lg border border-gray-200 p-6 shadow-sm"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Current Status</h2>
            <p className="text-sm text-gray-600">Track your progress through the pipeline</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[statusDisplay.color]}`}>
            {statusDisplay.label}
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
            <Clock className="text-blue-600 flex-shrink-0" size={20} />
            <div>
              <p className="text-sm font-medium text-gray-900">Next Step</p>
              <p className="text-xs text-gray-600">
                {profile?.paymentStatus === 'paid' 
                  ? 'Upload your required documents to proceed'
                  : 'Complete all free sections and proceed to payment'}
              </p>
            </div>
          </div>

          {profile?.paymentStatus !== 'paid' && (
            <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Payment Required</p>
                <p className="text-xs text-gray-600 mt-1 mb-3">
                  Complete the free sections to unlock document upload and interview scheduling. 
                  Payment is required to access these premium features.
                </p>
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="bg-[#202356] text-white font-semibold px-4 py-2 rounded-lg hover:bg-[#2542D4] transition-colors flex items-center gap-2 text-sm"
                >
                  <CreditCard size={16} />
                  <span>Pay $199 Now</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Journey Progress */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mt-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Journey</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {journeySteps.map((step, index) => {
            const Icon = step.icon;
            const isLocked = step.locked;
            const isCompleted = step.status === 'completed';
            const isInProgress = step.status === 'in-progress';

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 * index }}
                onClick={() => !isLocked && onNavigate && onNavigate(step.id)}
                className={`bg-white rounded-lg border p-4 transition-all ${
                  isLocked
                    ? 'border-gray-200 opacity-60 cursor-not-allowed'
                    : 'border-gray-200 hover:border-[#202356] hover:shadow-md cursor-pointer'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2 rounded-lg ${
                    isCompleted ? 'bg-green-100' :
                    isInProgress ? 'bg-blue-100' :
                    isLocked ? 'bg-gray-100' :
                    'bg-gray-100'
                  }`}>
                    <Icon 
                      size={20} 
                      className={
                        isCompleted ? 'text-green-600' :
                        isInProgress ? 'text-blue-600' :
                        isLocked ? 'text-gray-400' :
                        'text-gray-600'
                      }
                    />
                  </div>
                  {isLocked && <Lock size={16} className="text-gray-400" />}
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{step.title}</h3>
                <p className="text-xs text-gray-600">{step.description}</p>
                {!isLocked && (
                  <div className="mt-3 flex items-center text-xs text-[#202356] font-medium">
                    {isCompleted ? 'Completed' : isInProgress ? 'In Progress' : 'Get Started'}
                    <ArrowRight size={14} className="ml-1" />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Payment CTA for unpaid users - only show after completing first 3 sections */}
      {overview?.paymentStatus !== 'paid' && 
       overview?.sectionsCompleted?.personalDetails && 
       overview?.sectionsCompleted?.workHistory && 
       overview?.sectionsCompleted?.rolePreferences && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-6"
        >
          <div className="bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-lg p-6 text-white">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={20} />
                  <h3 className="text-lg font-semibold">Complete Your Work Abroad Journey</h3>
                </div>
                <p className="text-sm text-blue-100 mb-4">
                  Unlock document uploads and interview scheduling with top employers
                </p>
                <p className="text-xs text-gray-300 text-opacity-90 mb-4">
                  We'll manage your complete work abroad application from start to finish, beginning the moment you unlock access. Share your documents with us, and we'll connect you with our network of international employers. Through our direct relationships with recruitment agencies and companies, we understand what each employer is looking for and will position your application accordingly.
                </p>
                <ul className="space-y-2 text-xs text-gray-300 text-opacity-90 mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={14} />
                    <span>Upload documents to start your application</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={14} />
                    <span>Schedule interviews with top employers</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={14} />
                    <span>Benefit from our employer network insights</span>
                  </li>
                </ul>

                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="bg-white text-[#202356] font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  <CreditCard size={20} />
                  <span>Pay $199 Now</span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Important Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6"
      >
        <h3 className="font-semibold text-gray-900 mb-3">Important Information</h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <span>Complete all free sections before proceeding to payment</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <span>Payment unlocks document upload and interview scheduling</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <span>Interview slots are guaranteed for eligible candidates</span>
          </li>
          <li className="flex items-start gap-2">
            <AlertCircle size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <span className="font-medium">Job offers are not guaranteed and depend on employer decisions</span>
          </li>
        </ul>
      </motion.div>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
      />
    </div>
  );
};

export default WorkOverviewSection;
